/**
 * /finance (OCP-608) - the operator P&L: what the control plane costs per
 * month (servers, AI inference, mail, tooling, other) against what comes in
 * (Polar invoices net of the MoR fee), the margin, the 12-month trend, the
 * cost per active tenant and the AI margin. Manual cost rows are edited
 * here; derived rows (servers from vps_servers, AI from the gateway spend,
 * mail from the OMS send count) recompute nightly and on "Recompute".
 * Every write is the same call the `ocp_upsert_operator_cost` /
 * `ocp_delete_operator_cost` tools make.
 */
import { useCallback, useEffect, useState } from 'react';

import { BulkWalletCreditCard } from '../components/BulkWalletCreditCard';
import { api, ApiError } from '../lib/api';

export type CostCategory = 'servers' | 'ai_inference' | 'mail' | 'tooling' | 'other';
const CATEGORIES: CostCategory[] = ['servers', 'ai_inference', 'mail', 'tooling', 'other'];

export interface CostRow {
  id: string;
  month: string;
  category: CostCategory;
  provider: string;
  item: string;
  amount_eur_cents: number;
  source: 'manual' | 'derived';
  recurring: boolean;
  note: string | null;
  updated_at: string;
}

export interface Pnl {
  month: string;
  closed: boolean;
  costs: { total_eur_cents: number; by_category: Record<string, number>; rows: CostRow[] };
  revenue: {
    gross_eur_cents: number;
    fees_eur_cents: number;
    net_eur_cents: number;
    invoices: number;
    by_family: Record<string, number>;
    by_customer: Array<{ customer_id: string; email: string | null; tenants: string[]; net_eur_cents: number }>;
    fee_pct: number;
    fee_fixed_cents: number;
  };
  margin_eur_cents: number;
  margin_pct: number | null;
  active_tenants: number;
  cost_per_active_tenant_eur_cents: number | null;
  ai: { customer_charged_eur_cents: number; provider_cost_eur_cents: number; margin_eur_cents: number; margin_pct: number | null; sweeps: number; usd_eur_rate: number };
  derived_at: string | null;
}

export interface TrendRow { month: string; revenue_net_eur_cents: number; costs_eur_cents: number; margin_eur_cents: number; margin_pct: number | null }

export function fmtEur(cents: number | null | undefined): string {
  if (cents === null || cents === undefined || !Number.isFinite(cents)) return '-';
  return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
}

function currentMonth(): string {
  const d = new Date();
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}

const EMPTY_FORM = { category: 'servers' as CostCategory, provider: '', item: '', amount_eur: '', recurring: true, note: '' };

export function AdminFinancePage() {
  const [month, setMonth] = useState(currentMonth());
  const [pnl, setPnl] = useState<Pnl | null>(null);
  const [trend, setTrend] = useState<TrendRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [p, t] = await Promise.all([
        api<Pnl>(`/v1/admin/finance/pnl?month=${encodeURIComponent(month)}`),
        api<{ items: TrendRow[] }>('/v1/admin/finance/trend?months=12'),
      ]);
      setPnl(p);
      setTrend(t.items);
    } catch (err) {
      setError(err instanceof ApiError ? `Could not load the P&L (HTTP ${err.status}).` : 'Could not load the P&L.');
    }
  }, [month]);

  useEffect(() => {
    void load();
  }, [load]);

  const submit = async () => {
    const amount = Math.round(Number.parseFloat(form.amount_eur.replace(',', '.')) * 100);
    if (!form.provider.trim() || !form.item.trim() || !Number.isFinite(amount)) return;
    setBusy(true);
    setFlash(null);
    try {
      await api('/v1/admin/finance/costs', {
        method: 'POST',
        body: {
          month,
          category: form.category,
          provider: form.provider.trim(),
          item: form.item.trim(),
          amount_eur_cents: amount,
          recurring: form.recurring,
          note: form.note.trim() || null,
          reason: editingId ? 'edited on the Finance page' : 'entered on the Finance page',
        },
      });
      setForm({ ...EMPTY_FORM });
      setEditingId(null);
      setFlash(editingId ? 'Cost row updated.' : 'Cost row added.');
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Could not save the cost row (HTTP ${err.status}).` : 'Could not save the cost row.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (row: CostRow) => {
    setBusy(true);
    try {
      await api(`/v1/admin/finance/costs/${row.id}`, { method: 'DELETE', body: { reason: 'removed on the Finance page' } });
      setFlash(`Removed ${row.provider} / ${row.item}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Could not remove the cost row (HTTP ${err.status}).` : 'Could not remove the cost row.');
    } finally {
      setBusy(false);
    }
  };

  const derive = async () => {
    setBusy(true);
    setFlash(null);
    try {
      const r = await api<{ derived: number; alerts: number }>('/v1/admin/finance/derive', { method: 'POST', body: { month } });
      setFlash(`Recomputed ${r.derived} derived row(s)${r.alerts ? `, ${r.alerts} alert(s)` : ''}.`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? `Could not recompute (HTTP ${err.status}).` : 'Could not recompute.');
    } finally {
      setBusy(false);
    }
  };

  const input = 'rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-1 text-sm';

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Finance</h1>
          <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">
            Costs against revenue per month. Revenue is what Polar paid out (invoices net of the MoR fee); wallet top-ups count when bought. Derived rows recompute nightly.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-gray-700 dark:text-gray-300">
            Month{' '}
            <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className={input} data-testid="finance-month" />
          </label>
          <button type="button" onClick={() => void derive()} disabled={busy} className="rounded border border-gray-300 dark:border-gray-700 px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800/60 disabled:opacity-50" data-testid="finance-derive">
            Recompute derived
          </button>
        </div>
      </div>

      {error && <div role="alert" className="rounded border border-red-300 bg-red-50 dark:bg-red-900/30 px-3 py-2 text-sm text-red-700 dark:text-red-300">{error}</div>}
      {flash && <div className="text-sm text-gray-600 dark:text-gray-400" data-testid="finance-flash">{flash}</div>}

      {pnl && (
        <>
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4" data-testid="finance-kpis">
            <Kpi label="Revenue (net)" value={fmtEur(pnl.revenue.net_eur_cents)} hint={`gross ${fmtEur(pnl.revenue.gross_eur_cents)}, fees ${fmtEur(pnl.revenue.fees_eur_cents)}, ${pnl.revenue.invoices} invoice${pnl.revenue.invoices === 1 ? '' : 's'}`} />
            <Kpi label="Costs" value={fmtEur(pnl.costs.total_eur_cents)} hint={`${pnl.costs.rows.length} row${pnl.costs.rows.length === 1 ? '' : 's'}`} />
            <Kpi label="Margin" value={fmtEur(pnl.margin_eur_cents)} hint={pnl.margin_pct === null ? 'no revenue' : `${pnl.margin_pct.toFixed(1)} %`} negative={pnl.margin_eur_cents < 0} testId="finance-margin" />
            <Kpi label="Cost per active tenant" value={fmtEur(pnl.cost_per_active_tenant_eur_cents)} hint={`${pnl.active_tenants} active tenant${pnl.active_tenants === 1 ? '' : 's'}`} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <h2 className="text-sm font-semibold">Costs by category</h2>
              <table className="mt-2 w-full text-sm">
                <tbody>
                  {CATEGORIES.map((c) => (
                    <tr key={c} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1">{c.replace('_', ' ')}</td>
                      <td className="py-1 text-right tabular-nums" data-testid={`cost-cat-${c}`}>{fmtEur(pnl.costs.by_category[c] ?? 0)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
            <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <h2 className="text-sm font-semibold">Revenue by product family (net)</h2>
              <table className="mt-2 w-full text-sm">
                <tbody>
                  {Object.entries(pnl.revenue.by_family).length === 0 && (
                    <tr><td className="py-1 text-gray-500">No paid invoices this month.</td></tr>
                  )}
                  {Object.entries(pnl.revenue.by_family).map(([f, cents]) => (
                    <tr key={f} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1">{f}</td>
                      <td className="py-1 text-right tabular-nums">{fmtEur(cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <h3 className="mt-3 text-xs font-semibold uppercase tracking-wide text-gray-500">AI margin</h3>
              <p className="mt-1 text-sm" data-testid="finance-ai-margin">
                Customers charged {fmtEur(pnl.ai.customer_charged_eur_cents)}, provider cost {fmtEur(pnl.ai.provider_cost_eur_cents)} (USD at {pnl.ai.usd_eur_rate}), margin {fmtEur(pnl.ai.margin_eur_cents)}{pnl.ai.margin_pct !== null ? ` (${pnl.ai.margin_pct.toFixed(1)} %)` : ''} over {pnl.ai.sweeps} sweep{pnl.ai.sweeps === 1 ? '' : 's'}.
              </p>
            </section>
          </div>

          <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
            <h2 className="text-sm font-semibold">Cost rows {pnl.month}</h2>
            <table className="mt-2 w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500">
                <tr><th className="py-1">Category</th><th>Provider</th><th>Item</th><th className="text-right">Amount</th><th>Source</th><th>Note</th><th></th></tr>
              </thead>
              <tbody data-testid="finance-cost-rows">
                {pnl.costs.rows.map((r) => (
                  <tr key={r.id} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-1">{r.category.replace('_', ' ')}</td>
                    <td>{r.provider}</td>
                    <td>{r.item}</td>
                    <td className="text-right tabular-nums">{fmtEur(r.amount_eur_cents)}</td>
                    <td className="text-xs text-gray-500">{r.source}{r.recurring ? ', recurring' : ''}</td>
                    <td className="text-xs text-gray-500">{r.note ?? ''}</td>
                    <td className="text-right">
                      {r.source === 'manual' && (
                        <>
                          <button type="button" className="text-xs underline" onClick={() => { setEditingId(r.id); setForm({ category: r.category, provider: r.provider, item: r.item, amount_eur: (r.amount_eur_cents / 100).toFixed(2), recurring: r.recurring, note: r.note ?? '' }); }}>edit</button>
                          <button type="button" className="ml-2 text-xs text-red-600 underline" onClick={() => void remove(r)} disabled={busy} data-testid={`cost-remove-${r.id}`}>remove</button>
                        </>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex flex-wrap items-end gap-2" data-testid="finance-cost-form">
              <label className="text-xs text-gray-600">Category<br />
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value as CostCategory })} className={input} data-testid="cost-category">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
              </label>
              <label className="text-xs text-gray-600">Provider<br /><input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="STRATO" className={input} data-testid="cost-provider" /></label>
              <label className="text-xs text-gray-600">Item<br /><input value={form.item} onChange={(e) => setForm({ ...form, item: e.target.value })} placeholder="C8-100 SSD 1" className={input} data-testid="cost-item" /></label>
              <label className="text-xs text-gray-600">EUR / month<br /><input value={form.amount_eur} onChange={(e) => setForm({ ...form, amount_eur: e.target.value })} placeholder="49.00" className={`${input} w-24`} data-testid="cost-amount" /></label>
              <label className="flex items-center gap-1 text-xs text-gray-600"><input type="checkbox" checked={form.recurring} onChange={(e) => setForm({ ...form, recurring: e.target.checked })} /> recurring</label>
              <label className="text-xs text-gray-600">Note<br /><input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className={input} /></label>
              <button type="button" onClick={() => void submit()} disabled={busy} className="rounded bg-brand-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-600 disabled:opacity-50" data-testid="cost-save">
                {editingId ? 'Save row' : 'Add row'}
              </button>
              {editingId && <button type="button" className="text-xs underline" onClick={() => { setEditingId(null); setForm({ ...EMPTY_FORM }); }}>cancel</button>}
            </div>
          </section>

          <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
            <h2 className="text-sm font-semibold">12-month trend</h2>
            <table className="mt-2 w-full text-sm">
              <thead className="text-left text-xs uppercase tracking-wide text-gray-500"><tr><th className="py-1">Month</th><th className="text-right">Revenue (net)</th><th className="text-right">Costs</th><th className="text-right">Margin</th></tr></thead>
              <tbody data-testid="finance-trend">
                {trend.map((t) => (
                  <tr key={t.month} className="border-t border-gray-100 dark:border-gray-800">
                    <td className="py-1">{t.month}</td>
                    <td className="text-right tabular-nums">{fmtEur(t.revenue_net_eur_cents)}</td>
                    <td className="text-right tabular-nums">{fmtEur(t.costs_eur_cents)}</td>
                    <td className={`text-right tabular-nums ${t.margin_eur_cents < 0 ? 'text-red-700 dark:text-red-300' : ''}`}>{fmtEur(t.margin_eur_cents)}{t.margin_pct !== null ? ` (${t.margin_pct.toFixed(0)} %)` : ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          {pnl.revenue.by_customer.length > 0 && (
            <section className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
              <h2 className="text-sm font-semibold">Revenue by customer (net)</h2>
              <table className="mt-2 w-full text-sm">
                <tbody>
                  {pnl.revenue.by_customer.map((c) => (
                    <tr key={c.customer_id} className="border-t border-gray-100 dark:border-gray-800">
                      <td className="py-1">{c.email ?? c.customer_id}</td>
                      <td className="text-xs text-gray-500">{c.tenants.join(', ')}</td>
                      <td className="text-right tabular-nums">{fmtEur(c.net_eur_cents)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          )}
        </>
      )}

      {/* OCP-930 - operator bulk credit on the preview -> confirmation rail. */}
      <BulkWalletCreditCard />
    </div>
  );
}

function Kpi({ label, value, hint, negative, testId }: { label: string; value: string; hint?: string; negative?: boolean; testId?: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-3 shadow-sm">
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
      <div className={`mt-1 text-lg font-semibold tabular-nums ${negative ? 'text-red-700 dark:text-red-300' : 'text-gray-900 dark:text-gray-100'}`} data-testid={testId}>{value}</div>
      {hint && <div className="mt-0.5 text-xs text-gray-500">{hint}</div>}
    </div>
  );
}
